import angular from 'angular';
import AddMultipleUsersTable from '@sdk/users/components/AddMultipleUsersTable';

import employeeEmptySearchResultsComponent from '@web/users/EmptyStates/EmployeeEmptySearchResults/EmployeeEmptySearchResultsAngular';
import employeeEmptyStateComponent from '@web/users/EmptyStates/EmployeeEmptyState/EmployeeEmptyStateAngular';
import UserMergeModal from '@web/users/UserMerge/UserMergeModalAngular';
import userDocumentsViewAngular from '@web/users/documents/UserDocumentsView/UserDocumentsViewAngular';
import r2a from '@web/angularIntegration/r2a';

import { STATE_DEFINITION_COMMON } from '../../../users/constants';

import ManageUserListCtrl from './ManageUserListCtrl';
import CreateUserCtrl from './CreateUserCtrl';
import CreateMultipleUsersCtrl from './CreateMultipleUsersCtrl';
import EditUserProfileCtrl from './EditUserProfileCtrl';
import ImportUsersCtrl from './ImportUsersCtrl';
import createMapping from './directives/createMapping';
import addUserFooterButtonsComponent from './components/addUserFooterButtonsComponent';
import userHeaderComponent from './components/userHeaderComponent';
import employeeFiltersComponent from './components/employeeFiltersComponent';
import editSalariesComponent from './components/editSalariesComponent';
import listUrl from './manage-user-list.html';
import createUrl from './create.html';
import createMultipleUsersUrl from './createMultipleUsers.html';
import editUrl from './edit.html';
import importUrl from './modal-users-import.html';
import wagesHistoryUrl from './wagesHistory.html';
import UserWagesHistoryCtrl from './UserWagesHistoryCtrl';
import UserUtilsImport from './services/UserUtils';
import SelfInviteService from './services/SelfinviteService';
import selfInviteEmptyListComponent from './selfInvite/selfInviteEmptyListComponent';
import usersImportTableComponent from './components/usersImportTableComponent';
import orgInviteUrlModalComponent from './components/orgInviteUrlModalComponent';
import userBaseWageComponent from './components/userBaseWageComponent';
import usersTableColumnComponent from './directives/usersTableColumn';
import EmployeeSearchService from './services/EmployeeSearchService';

const usersModule = angular.module('sling.dashboard.manage.users', []);

usersModule.config(($stateProvider) => {
  $stateProvider.state('dashboard.manage.users', {
    abstract: true,
    url: '/users',
    template: '<ui-view></ui-view>',
  });

  $stateProvider.state('dashboard.manage.users.list', {
    url: '/list?showInviteTooltip?tab',
    templateUrl: listUrl,
    controller: ManageUserListCtrl,
    controllerAs: 'ctrl',
    data: {
      title: 'Users',
    },
  });

  $stateProvider.state('dashboard.manage.users.add', {
    url: '/add',
    templateUrl: createUrl,
    controller: CreateUserCtrl,
    controllerAs: 'ctrl',
    resolve: {
      geo(RestUtils) {
        'ngInject';

        return RestUtils.getGeo();
      },
      user(PersonaCollection, UserUtils) {
        'ngInject';

        return {
          role: 'user',
          accessToLaborCost: true, // relevant only if user ends up to be a manager
          locations: UserUtils.getLocationsForNewUser(
            PersonaCollection.me,
            PersonaCollection.locations
          ),
          positions: [],
          groups: [],
          invite: false,
          hiddenOnSchedule: false,
        };
      },
      previousState($transition$, $state) {
        const from = $transition$.from();
        return {
          name: from.name,
          params: { ...$state.params },
        };
      },
      $uibModalInstance() {
        return null;
      },
    },
  });

  $stateProvider.state('dashboard.manage.users.add-multiple', {
    url: '/add-multiple',
    templateUrl: createMultipleUsersUrl,
    controller: CreateMultipleUsersCtrl,
    controllerAs: 'ctrl',
    resolve: {
      geo(RestUtils) {
        'ngInject';

        return RestUtils.getGeo();
      },
      user(PersonaCollection, UserUtils) {
        'ngInject';

        return {
          role: 'user',
          locations: UserUtils.getLocationsForNewUser(
            PersonaCollection.me,
            PersonaCollection.locations
          ),
          positions: PersonaCollection.positions,
          groups: [],
        };
      },
      previousState($transition$, $state) {
        const from = $transition$.from();
        return {
          name: from.name,
          params: { ...$state.params },
        };
      },
      $uibModalInstance() {
        return null;
      },
    },
  });

  $stateProvider.state('dashboard.manage.users.profile', {
    ...STATE_DEFINITION_COMMON['users.profile'],
    views: {
      '@': {
        templateUrl: editUrl,
        controller: EditUserProfileCtrl,
        controllerAs: 'ctrl',
      },
    },
  });

  $stateProvider.state('dashboard.manage.users.profile.wages-history', {
    url: '/wages-history',
    resolve: {
      user(personaCollection, PersonaCollection, $transition$) {
        'ngInject';

        return PersonaCollection.personasDict[$transition$.params().userId];
      },
    },
    views: {
      '@': {
        templateUrl: wagesHistoryUrl,
        controller: UserWagesHistoryCtrl,
        controllerAs: 'ctrl',
      },
    },
  });

  $stateProvider.state('dashboard.manage.users.profile.unavailability-history', {
    url: '/unavailability-history',
    views: {
      '@': {
        template: '<unavailability-history-screen></unavailability-history-screen>',
      },
    },
  });

  $stateProvider.state('dashboard.manage.users.list.import', {
    url: '/import',
    onEnter($uibModal, $state) {
      'ngInject';

      $uibModal
        .open({
          size: 'lg',
          templateUrl: importUrl,
          controller: ImportUsersCtrl,
          controllerAs: 'ctrl',
          windowClass: 'upload-sales',
          resolve: {
            geo(RestUtils) {
              return RestUtils.getGeo();
            },
          },
        })
        .result.ignoreCancel()
        .finally(() => {
          $state.transitionTo('dashboard.manage.users.list');
        });
    },
  });
});

usersModule.run(($transitions, Authentication, PersonaCollection, UserUtils) => {
  $transitions.onEnter(
    {
      to: 'dashboard.manage.users.profile.wages-history',
    },
    (transition) => {
      const { hasAccessToLaborCost, canSeeOwnWage } = Authentication.user;
      const userId = transition.params('to').userId;
      const user = PersonaCollection.personasDict[userId];
      const canSeeHistoricalWages =
        (user.isMe && canSeeOwnWage) || (UserUtils.canEditUserWages(user) && hasAccessToLaborCost);

      if (!canSeeHistoricalWages) {
        return transition.router.stateService.target('dashboard.manage.users.profile', {
          userId,
          tab: 'profile',
        });
      }
    }
  );
});

usersModule.service('UserUtils', UserUtilsImport);
usersModule.service('SelfInviteService', SelfInviteService);
usersModule.service('EmployeeSearchService', EmployeeSearchService);
usersModule.directive('createMapping', createMapping);
usersModule.directive('usersTableColumn', usersTableColumnComponent);
usersModule.component('addUserFooterButtons', addUserFooterButtonsComponent);
usersModule.component('userHeader', userHeaderComponent);
usersModule.component('employeeFilters', employeeFiltersComponent);
usersModule.component('usersImportTable', usersImportTableComponent);
usersModule.component('editSalaries', editSalariesComponent);
usersModule.component('orgInviteUrlModal', orgInviteUrlModalComponent);
usersModule.component('userBaseWage', userBaseWageComponent);
usersModule.component('selfInviteEmptyList', selfInviteEmptyListComponent);
usersModule.component(
  'rAddMultipleUsersTable',
  r2a(AddMultipleUsersTable, [
    'locations',
    'positions',
    'isUserUnverified',
    'userCountryCode',
    'userCountryIsoCode',
    '&onSuccess',
    '&onCancel',
    '&onError',
  ])
);
usersModule.component('rEmployeeEmptySearchResults', r2a(employeeEmptySearchResultsComponent));
usersModule.component('rEmployeeEmptyState', r2a(employeeEmptyStateComponent));
usersModule.component('rUserMergeModal', UserMergeModal);
usersModule.component('rUserDocumentsView', r2a(userDocumentsViewAngular));

export default usersModule.name;
